import { memo, useLayoutEffect, useRef, useState } from 'react';
import { KEY_ROWS, FINGER_OF, QWERTY, shiftCodeFor, isShiftCode } from '../engine/keyboardGeometry';
import { fingerColor } from './fingers';

const UNIT = 44; // px per 1u at scale 1
const GAP = 6;
const keyWidth = (w = 1) => w * UNIT + (w - 1) * GAP;
const NATURAL_WIDTH = Math.max(...KEY_ROWS.map((row) => row.reduce((sum, k) => sum + keyWidth(k.w), 0) + (row.length - 1) * GAP));
const NATURAL_HEIGHT = KEY_ROWS.length * UNIT + (KEY_ROWS.length - 1) * GAP;

/**
 * On-screen keyboard. Keys are coloured by finger zone and the next key to press
 * glows; when that key needs Shift, the opposite-hand Shift glows too. In Bijoy the
 * legend shows the Bangla a key prints, with the shifted character above it.
 *
 * The board is drawn at a fixed size and scaled to fit its container (up to
 * `maxScale`, and within `maxHeight` px when given), so it stays a real keyboard
 * shape from a phone to a wide monitor.
 *
 * `legend(code)` → { plain, shift } | null, from the active layout.
 * `nextStroke` = { code, shift } | null, the key the guide wants next.
 * `heat(code)` → 0..1 | null, optional accuracy tint for the heat-map.
 */
function Keyboard({ legend, nextStroke, pressedCode, showFingerColors = true, heat, dimInactive = false, activeCodes = null, maxScale = 1, maxHeight = null }) {
    const nextCode = nextStroke?.code || null;
    const shiftGlow = nextStroke && nextStroke.shift ? shiftCodeFor(nextStroke.code) : null;
    const wrapRef = useRef(null);
    const [scale, setScale] = useState(1);

    useLayoutEffect(() => {
        const el = wrapRef.current;
        if (!el) return undefined;
        // Fit the width, and the height left for it when the caller gives one.
        const fit = () => setScale(Math.min(maxScale, el.clientWidth / NATURAL_WIDTH, maxHeight ? maxHeight / NATURAL_HEIGHT : Infinity));
        fit();
        const ro = new ResizeObserver(fit);
        ro.observe(el);
        return () => ro.disconnect();
    }, [maxScale, maxHeight]);

    return (
        <div ref={wrapRef} className="w-full select-none" aria-hidden="true">
            <div className="mx-auto" style={{ width: NATURAL_WIDTH * scale, height: NATURAL_HEIGHT * scale }}>
                <div
                    className="flex flex-col"
                    style={{ width: NATURAL_WIDTH, gap: GAP, transform: `scale(${scale})`, transformOrigin: 'top left' }}
                >
                    {KEY_ROWS.map((row, r) => (
                        <div key={r} className="flex justify-center" style={{ gap: GAP }}>
                            {row.map((key) => {
                                const leg = legend?.(key.code);
                                const finger = FINGER_OF[key.code];
                                const isNext = key.code === nextCode;
                                const isShiftGlow = isShiftCode(key.code) && key.code === shiftGlow;
                                const isPressed = key.code === pressedCode;
                                const inactive = dimInactive && activeCodes && !activeCodes.has(key.code) && !key.modifier;
                                const color = showFingerColors && finger && !key.modifier ? fingerColor(finger) : null;
                                const heatVal = heat ? heat(key.code) : null;

                                let cls = 'border-[#E5DFD8] bg-white text-[#3D3833] shadow-[0_2px_0_#E5DFD8] dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-200 dark:shadow-[0_2px_0_#18181b]';
                                const style = { width: keyWidth(key.w), height: UNIT };
                                if (heatVal != null) {
                                    const hue = Math.round(heatVal * 120); // green (good) → red (weak)
                                    style.backgroundColor = `hsl(${hue} 70% 92%)`;
                                    cls = 'border-[#E5DFD8] text-[#3D3833] shadow-[0_2px_0_#E5DFD8] dark:text-zinc-900';
                                }
                                if (inactive) cls = 'border-[#EFEBE7] bg-[#F7F5F2] text-[#CFC8C0] dark:border-zinc-800 dark:bg-zinc-900/70 dark:text-zinc-600';
                                if (isNext) {
                                    cls = 'z-10 -translate-y-px border-primary bg-primary text-white shadow-[0_2px_0_#b36b00,0_0_0_4px_rgba(230,138,0,0.22),0_8px_22px_rgba(230,138,0,0.35)]';
                                } else if (isShiftGlow) {
                                    cls = 'border-primary/70 bg-primary/15 text-primary shadow-[0_2px_0_rgba(230,138,0,0.4)] dark:text-orange-300';
                                } else if (isPressed) {
                                    cls = 'translate-y-[2px] border-primary/40 bg-primary/10 text-[#3D3833] shadow-none dark:text-zinc-100';
                                }

                                return (
                                    <div
                                        key={key.code}
                                        className={`relative flex items-center justify-center rounded-[9px] border text-[12px] font-semibold transition-[transform,background-color,box-shadow] duration-75 ${cls}`}
                                        style={style}
                                    >
                                        {isNext && <span className="pointer-events-none absolute inset-0 animate-ping rounded-[9px] border-2 border-primary/50 [animation-duration:1.4s]" />}
                                        {key.modifier ? (
                                            <span className={`text-[10px] font-medium ${isNext || isShiftGlow ? '' : 'opacity-70'}`}>{key.label}</span>
                                        ) : leg ? (
                                            <span className="flex flex-col items-center leading-none">
                                                {leg.shift && leg.shift !== leg.plain ? (
                                                    <span className={`text-[9.5px] leading-none ${isNext ? 'text-white/80' : 'text-[#ABA39B] dark:text-zinc-500'}`}>{leg.shift}</span>
                                                ) : null}
                                                <span className="font-hind text-[14px] leading-tight">{leg.plain}</span>
                                            </span>
                                        ) : (
                                            <span className="font-hind text-[13px]">{(QWERTY[key.code] || [''])[0]}</span>
                                        )}
                                        {key.bump && <span className={`absolute bottom-[7px] h-[2px] w-2.5 rounded-full ${isNext ? 'bg-white/80' : 'bg-[#BDB5AD] dark:bg-zinc-500'}`} />}
                                        {color && !isNext && !inactive && (
                                            <span className="absolute inset-x-2 bottom-[3px] h-[3px] rounded-full opacity-90" style={{ backgroundColor: color }} />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default memo(Keyboard);
